import type { DraftState, PoolSuggestion, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { DEFAULT_PLATE_OPTIONS, detectPlates } from '../../Util/orderSettingDetection/plateDetection.ts'
import { plural } from '../../Util/plural.ts'

/** The groups that are or will be items, in file order: not those deleted in the classic view. */
export const placedGroups = (draft: DraftState): WizardSampleGroup[] => {
  const position = new Map(draft.fileOrder.map((key, index) => [key, index]))
  return Object.values(draft.groups)
    .filter((group) => group.item.status !== 'removed')
    .sort((a, b) => (position.get(a.key) ?? draft.fileOrder.length) - (position.get(b.key) ?? draft.fileOrder.length))
}

/** Whether the file names could suggest groups at all: `detectPlates` keeps smaller orders whole. */
export const suggestionPossible = (draft: DraftState): boolean => placedGroups(draft).length >= DEFAULT_PLATE_OPTIONS.minFilesToSplit

/** Whether the suggestion still waits for some file's sequencing run. */
export const runsPending = (draft: DraftState): boolean => placedGroups(draft).some((group) => group.run === undefined)

/** Pool numbers as 0, 1, 2… in the order of the numbers given, so the first pool stays the order's first. */
export const compactPools = (pools: Record<string, number>): Record<string, number> => {
  const numbers = [...new Set(Object.values(pools))].sort((a, b) => a - b)
  return Object.fromEntries(Object.entries(pools).map(([key, pool]) => [key, numbers.indexOf(pool)]))
}

export const poolsWithoutControl = (draft: DraftState, pools: Record<string, number>): number[] => {
  const count = Math.max(-1, ...Object.values(pools)) + 1
  const withControl = new Set(
    Object.entries(pools)
      .filter(([key]) => draft.groups[key]?.itemType === 'NEGATIVE_CONTROL')
      .map(([, pool]) => pool)
  )
  return Array.from({ length: count }, (_, pool) => pool).filter((pool) => !withControl.has(pool))
}

/**
 * The groups the file names suggest (`detectPlates`: well positions, else Illumina sample numbers per
 * run), offered as pools; `undefined` when the names carry neither or suggest a single group. Only an
 * offer: it matches the pools in about three of four multi-pool orders of the one lab it was measured on.
 */
export const poolSuggestion = (draft: DraftState): PoolSuggestion | undefined => {
  const groups = placedGroups(draft)
  const result = detectPlates(groups.map((group) => ({ name: draft.files[group.forward]?.name ?? group.key, run: group.run ?? '' })))
  if (result.verdict !== 'PLATES' || result.method === null || result.method === 'file-count') {
    return undefined
  }
  const pools: Record<string, number> = {}
  result.plates.forEach((indexes, pool) => indexes.forEach((index) => (pools[groups[index].key] = pool)))
  return {
    pools,
    count: result.plates.length,
    method: result.method,
    withoutControl: poolsWithoutControl(draft, pools)
  }
}

export const poolSizes = (pools: Record<string, number>): number[] => {
  const sizes: number[] = []
  Object.values(pools).forEach((pool) => {
    sizes[pool] = (sizes[pool] ?? 0) + 1
  })
  return Array.from(sizes, (size) => size ?? 0)
}

/**
 * Pools the wizard may remove because nothing is or will be in them: never the first (the files were
 * uploaded to it), not while an item is still being created or waits to move into it, and not one
 * whose removal failed before.
 */
export const removablePools = (draft: DraftState): string[] => {
  const groups = placedGroups(draft)
  /* a removal not through yet still has its item in some pool */
  if (groups.some((group) => group.item.status !== 'created') || Object.keys(draft.removals ?? {}).length > 0) {
    return []
  }
  return draft.poolIds.filter(
    (id, pool) =>
      pool > 0 &&
      !!id &&
      !(draft.stuckPools ?? []).includes(id) &&
      !groups.some((group) => (group.pool ?? 0) === pool || group.item.poolId === id)
  )
}

/** Each placed group's pool as the draft has it now, numbered 0, 1, 2… */
export const currentPools = (draft: DraftState): Record<string, number> =>
  compactPools(Object.fromEntries(placedGroups(draft).map((group) => [group.key, group.pool ?? 0] as const)))

/** A pool index after all of `pools`, so a move or drop there never joins one. */
export const nextPoolAfter = (pools: Iterable<number>): number => {
  let last = 0
  for (const pool of pools) {
    last = Math.max(last, pool)
  }
  return last + 1
}

/** The pool a "new pool" drop targets: after every pool in use, so it never joins one. */
export const nextDropPool = (draft: DraftState): number => nextPoolAfter(placedGroups(draft).map((group) => group.pool ?? 0))

/** A pool index, or a new pool after every pool there is. */
export type PoolTarget = number | 'new'

export type PoolChoice = {
  /** The pool index the groups carry. */
  pool: number
  /** 1-based, as the lab sees it. */
  number: number
  items: number
  /** The sample sheet's name for the pool, when it gives one. */
  name?: string
}

/**
 * Pools as the lab sees them, numbered 1, 2, … in index order; the one numbering every pool list, label
 * and "new pool" number uses, whether of the draft's pools or of the board's unapplied columns.
 */
export const poolList = (sizes: Iterable<readonly [pool: number, items: number]>): PoolChoice[] =>
  [...sizes].sort(([a], [b]) => a - b).map(([pool, items], index) => ({ pool, number: index + 1, items }))

/** The number a new pool gets after `pools`. */
export const newPoolNumber = (pools: readonly PoolChoice[]): number => pools.length + 1

export const poolLabel = (number: number): string => `Pool ${number}`

/**
 * A pool as the lab sees it named, in every list, label and drop zone: the sheet's name, else its number
 * ("Pool 2", or "pool 2" inside a sentence).
 */
export const poolTitle = (choice: Pick<PoolChoice, 'number' | 'name'>, { inSentence = false } = {}): string =>
  choice.name ?? (inSentence ? `pool ${choice.number}` : poolLabel(choice.number))

/** `choices` with the names `names` gives by pool index. */
export const withPoolNames = (choices: readonly PoolChoice[], names: Readonly<Record<number, string>>): PoolChoice[] =>
  choices.map((choice) => (names[choice.pool] ? { ...choice, name: names[choice.pool] } : choice))

/** The pools in use, in number order, with how many items each holds. */
export const poolChoices = (draft: DraftState): PoolChoice[] => {
  const counts = new Map<number, number>()
  for (const group of placedGroups(draft)) {
    const pool = group.pool ?? 0
    counts.set(pool, (counts.get(pool) ?? 0) + 1)
  }
  return poolList(counts)
}

export type PoolContents = {
  /** 0-based, as shown: "Pool 1" is pool 0. */
  pool: number
  /** Every group key in the pool, in file order. */
  keys: string[]
  samples: number
  /** The pool's negative controls' group keys, in file order. */
  controls: string[]
}

/** What each pool holds of the items in `order`, pools in number order; `pools` must be compact. */
export const contentsOf = (
  order: readonly string[],
  isControl: (key: string) => boolean,
  pools: Readonly<Record<string, number>>
): PoolContents[] => {
  const result: PoolContents[] = []
  for (const key of order) {
    const pool = pools[key] ?? 0
    const contents = (result[pool] ??= { pool, keys: [], samples: 0, controls: [] })
    contents.keys.push(key)
    if (isControl(key)) {
      contents.controls.push(key)
    } else {
      contents.samples++
    }
  }
  return result.filter(Boolean)
}

/** What each pool holds, pools in number order and items in file order; `pools` must be compact. */
export const poolContents = (draft: DraftState, pools: Record<string, number>): PoolContents[] =>
  contentsOf(
    placedGroups(draft).map((group) => group.key),
    (key) => draft.groups[key].itemType === 'NEGATIVE_CONTROL',
    pools
  )

/** "3 samples · 1 control": how the wizard counts items wherever it does. */
export const itemCounts = (samples: number, controls: number): string => `${plural(samples, 'sample')} · ${plural(controls, 'control')}`

/** A pool whose samples no negative control of its own covers. */
export const lacksControl = (contents: PoolContents): boolean => contents.controls.length === 0

/** "pool 2", "pools 2 and 3", "pools 1, 2 and 4" for 0-based pools. */
export const poolNames = (pools: readonly number[]): string => {
  const numbers = pools.map((pool) => pool + 1)
  if (numbers.length <= 1) {
    return `pool ${numbers[0] ?? ''}`.trim()
  }
  return `pools ${numbers.slice(0, -1).join(', ')} and ${numbers.at(-1)}`
}
